<script setup lang="ts">
import type { ProBodySlots, ProWatermarkOptions } from '../interfaces';
import { bem } from '@hlx/use';
import { BEM_XOM_NS } from '@inn/const';
import { computed } from 'vue';
import { useLayoutState } from '../composables';
import XomLayoutCopyright from './copyright.vue';

defineOptions({ name: 'XomProLayoutBody' });
defineSlots<ProBodySlots>();

const ns = bem('layout-body', BEM_XOM_NS);
const { effective, watermark, title, copyright, icp } = useLayoutState();

const watermark_ = computed<ProWatermarkOptions>(() => {
  const _ = watermark.value;
  if (!_) {
    return;
  }
  return typeof _ === 'object' ? _ : { content: typeof _ === 'string' ? _ : title.value };
});

const isFixedBody = computed(() => effective.body === 'fixed' && effective.layout !== 'mix');
const isFixedFooter = computed(() => effective.fixedFoot);
</script>

<template>
  <a-layout-content
    :class="[ns.b(), ns.is('fixed', isFixedBody), ns.is('unfixed-footer', !isFixedFooter)]"
  >
    <div :class="ns.e('content')">
      <a-watermark
        v-if="effective.showWatermark && watermark_"
        :class="ns.e('watermark')"
        v-bind="watermark_"
      >
        <slot />
      </a-watermark>

      <slot v-else />

      <template v-if="effective.fixedFoot && copyright !== false && icp !== false">
        <slot name="copyright">
          <xom-layout-copyright />
        </slot>
        <slot name="icp" />
      </template>
    </div>
  </a-layout-content>
</template>

<style scoped lang="scss">
@use '@inn/assets/scss/vars';
@use '@inn/assets/scss/mixins';

@include mixins.b(layout-body) {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 0;

  @include mixins.e(watermark) {
    display: flex;
    flex: auto;
    flex-flow: column;
    height: 100%;
    min-height: 0;
  }

  @include mixins.e('content') {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    align-items: center;
    width: 100%;
    min-height: 0;
  }

  @include mixins.when(fixed) {
    min-height: 0;

    @include mixins.e('content') {
      > * {
        max-width: var(--xom-layout-body-fixed-width);
      }
    }
  }

  @include mixins.when(unfixed-footer) {
    min-height: auto;

    @include mixins.e('content') {
      min-height: auto;
    }
  }
}
</style>
